import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  Pagination,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";
import {
  IconAdjustments,
  IconArrowBackUp,
  IconArrowsSort,
  IconBuildingStore,
  IconCoin,
  IconDeviceFloppy,
  IconGripVertical,
  IconLink,
  IconPhoto,
  IconPlus,
  IconQrcode,
  IconSearch,
  IconSend,
  IconTrash,
} from "@tabler/icons-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";
import { EmojiPicker } from "@/components/EmojiPicker";
import { ModalTitle } from "@/components/ModalTitle";
import {
  isMapsHref,
  isMobileTel,
  normalizeLinkValue,
  waLinkFromTel,
} from "@/lib/linkValue";
import { BRAND_PREFIX } from "@/lib/brandEmojis";
import {
  createBranch,
  deleteBranch,
  getBranchOverrides,
  resetBranchProductRanks,
  setBranchProductRanks,
  getBranches,
  pushBranchProductPrice,
  updateBranch,
  updateBranchRank,
  removeBranchImage,
  uploadBranchImage,
  upsertBranchOverride,
} from "@/api/branches";
import { getConnections, getMappings } from "@/api/connections";
import { getProducts } from "@/api/products";
import { getCategories } from "@/api/category";
import { useAuthStore } from "@/stores/auth";
import { notify } from "@/lib/notify";
import { pushSummary } from "@/lib/pushResult";
import {
  ListCell,
  ListEmpty,
  ListError,
  ListHeader,
  ListRow,
  ListScroll,
} from "@/components/DataList";
import { byBranchRank, moveBeside } from "@/lib/branchOrder";
import { BranchCategoryOrder } from "./BranchCategoryOrder";
import { FormModalFooter } from "@/components/FormModalFooter";
import { PER_PAGE_OPTIONS } from "@/lib/format";
import { PageTitle } from "@/components/PageTitle";
import { QrModal } from "@/components/QrModal";
import { ImageUploadModal } from "@/components/ImageUploadModal";
import { IMAGE_TARGETS } from "@/lib/imageTargets";
import { SortableBranchRow } from "./SortableBranchRow";
import { EMPTY_LIST } from "@/lib/queryClient";

const slugHint =
  "Küçük harf ve tire: ör. kadikoy, merkez-sube. QR adresi olur.";

// Sabit tür listesi (phone/instagram/…) KALDIRILDI — panelde de menüde de. Anahtar artık
// kullanıcının seçtiği simge: bir emoji ya da `brand:` önekli marka simgesi (lib/brandEmojis).
//
// Bedeli şema türetiminin kaybı: 'phone' tel:, 'whatsapp' wa.me/, 'email' mailto: üretiyordu, simge
// hiçbirini taşımıyor. Adres bu yüzden TAM yazılıyor (bkz. satırdaki placeholder).
//
// Kayıtlı eski anahtarlar artık tanınmıyor; prod verisi elle düzeltilecek.
const MAX_LINKS = 8;

// Yeni satırın başlangıç emojisi. Nötr bilerek: kullanıcı ne eklediğini kendi seçsin.
const DEFAULT_LINK_EMOJI = "🔗";

// Satırın sürükleme kimliği. Eskiden `link.icon` kullanılıyordu çünkü türler benzersizdi (aynı
// tür iki kez eklenemiyordu). Anahtar artık serbest emoji — iki bağlantı pekâlâ aynı emojiyi
// taşıyabilir ve o zaman hem React anahtarları hem dnd-kit kimlikleri çakışırdı: satırlar
// birbirinin yerine geçer, sürükleme yanlış satırı taşırdı.
const linkUid = () => crypto.randomUUID();

const withUid = (links) =>
  (links ?? []).map((link) => ({ ...link, uid: linkUid() }));

// Konum satırı: menünün ikon satırında GÖRÜNMEZ, yalnızca şube seçim kartındaki yol tarifini
// besler. Önce simgeden tanınıyor, sonra adresten — kullanıcı yuvaya yarım bir adres yazdığında
// satır listeye kaçmasın; adres kontrolü 'maps' anahtarıyla yazılmış eski kayıtlar için.
const isLocationLink = (link) =>
  link.icon === `${BRAND_PREFIX}maps` ||
  isMapsHref(normalizeLinkValue(link.value ?? ""));

// 8'lik sınır menüde GÖRÜNENLERE işliyor; konum sayılmıyor.
const menuLinkCount = (links) =>
  links.filter((link) => !isLocationLink(link)).length;

// Sekme başlığındaki hata işareti. Metin değil nokta: sekme etiketi zaten dar ve hangi alanın
// hatalı olduğu sekme açılınca alanın kendi altında yazıyor.
const ErrorDot = () => (
  <Box w={6} h={6} bg="red.6" style={{ borderRadius: "50%" }} />
);

// Sürüklenebilir bağlantı satırı: grip + emoji + adres + aktif/pasif toggle + sil.
function SortableLinkRow({ id, index, form }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const link = form.values.links[index];
  const active = link.active !== false;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  // Öneri yalnızca CEP numarasında ve o şubede henüz WhatsApp bağlantısı yokken.
  const tel = normalizeLinkValue(link.value);
  const hasWhatsapp = form.values.links.some((item) =>
    /\/\/(wa\.me|api\.whatsapp\.com)\b/i.test(item.value ?? ""),
  );
  const suggestWhatsapp =
    isMobileTel(tel) &&
    !hasWhatsapp &&
    menuLinkCount(form.values.links) < MAX_LINKS;

  const addWhatsapp = () =>
    form.insertListItem("links", {
      uid: linkUid(),
      icon: `${BRAND_PREFIX}whatsapp`,
      value: waLinkFromTel(tel),
      active: true,
    });

  return (
    <Group
      ref={setNodeRef}
      style={style}
      gap="xs"
      wrap="nowrap"
      align="flex-start"
    >
      <ActionIcon
        variant="subtle"
        color="gray"
        mt={4}
        style={{ cursor: "grab" }}
        title="Sürükle"
        {...attributes}
        {...listeners}
      >
        <IconGripVertical size={16} />
      </ActionIcon>
      <EmojiPicker
        value={link.icon}
        onChange={(emoji) => form.setFieldValue(`links.${index}.icon`, emoji)}
      />
      {/* Adres TAM saklanıyor: tür listesi kalktığı için şema (tel:, mailto:) anahtardan
                türetilemiyor — emoji bir şema taşımıyor.
                Ama kullanıcıdan `tel:+90...` yazmasını beklemiyoruz: alandan çıkınca düz numara
                `tel:`e, e-posta `mailto:`ya çevriliyor (lib/linkValue) ve sonucu gözüyle görüyor.
                Bu olmadan "5075971811" menüde `https://5075971811` oluyor ve kartta telefon çipi
                çıkmıyordu — çip tespiti adresin şemasına bakıyor. */}
      <TextInput
        style={{ flex: 1, opacity: active ? 1 : 0.5 }}
        placeholder="Numara, e-posta ya da adres"
        {...form.getInputProps(`links.${index}.value`)}
        onBlur={(event) => {
          // getInputProps'un kendi onBlur'u "touched" işaretini koyuyor; ezmeyip
          // üzerine ekliyoruz.
          form.getInputProps(`links.${index}.value`).onBlur?.(event);
          form.setFieldValue(
            `links.${index}.value`,
            normalizeLinkValue(event.currentTarget.value),
          );
        }}
      />
      {/* Cep numarası varsa WhatsApp'ı TEKLİF ediyoruz, otomatik eklemiyoruz: numaranın
                WhatsApp'ta kayıtlı olduğunu doğrulayamıyoruz ve ölü bir çip hiç çip olmamasından
                kötü. Sabit hatta hiç çıkmıyor (bkz. isMobileTel). */}
      {suggestWhatsapp && (
        <Tooltip label="WhatsApp bağlantısı olarak da ekle" withArrow>
          <ActionIcon
            aria-label="WhatsApp bağlantısı olarak da ekle"
            variant="light"
            color="teal"
            mt={4}
            onClick={addWhatsapp}
          >
            <img src="/brands/whatsapp.svg" alt="" width={16} height={16} />
          </ActionIcon>
        </Tooltip>
      )}
      <Tooltip
        label={active ? "Menüde görünür — gizlemek için kapat" : "Menüde gizli"}
        withArrow
      >
        <Switch
          mt={6}
          size="sm"
          color="teal"
          checked={active}
          onChange={(event) =>
            form.setFieldValue(
              `links.${index}.active`,
              event.currentTarget.checked,
            )
          }
        />
      </Tooltip>
      <ActionIcon
        variant="subtle"
        color="red"
        mt={4}
        onClick={() => form.removeListItem("links", index)}
        title="Kaldır"
      >
        <IconTrash size={16} />
      </ActionIcon>
    </Group>
  );
}

function BranchFormModal({ opened, onClose, branch }) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(branch);

  const form = useForm({
    initialValues: {
      name: "",
      slug: "",
      links: [],
      settings: {
        showBranches: true,
        showLinks: true,
        showPriceDate: false,
        priceUpdatedAt: dayjs().format("YYYY-MM-DD"),
        gaMeasurementId: "",
        reviewPlaceId: "",
        theme: "default",
      },
    },
    validate: {
      name: (value) => (value.trim() ? null : "Şube adı gerekli"),
      slug: (value) =>
        /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value) ? null : "Geçersiz slug",
      "settings.priceUpdatedAt": (value, values) =>
        values.settings.showPriceDate && !value ? "Tarih seçin" : null,
      "settings.gaMeasurementId": (value) =>
        !value || /^G-[A-Z0-9]+$/.test(value.trim())
          ? null
          : "G-XXXX biçiminde olmalı",
      // API ile aynı kural. Yanlış bir değer kabul edilirse müşteri puanlama ekranı yerine
      // hata sayfasına düşer, yani özellik sessizce boşa gider.
      "settings.reviewPlaceId": (value) =>
        !value?.trim() || /^[A-Za-z0-9_-]{15,}$/.test(value.trim())
          ? null
          : "Geçerli bir Place ID olmalı",
    },
  });

  useEffect(() => {
    if (!opened) {
      return;
    }
    if (branch) {
      form.setValues({
        name: branch.name ?? "",
        slug: branch.slug ?? "",
        links: withUid(branch.links),
        settings: {
          showBranches: branch.settings?.showBranches !== false,
          showLinks: branch.settings?.showLinks !== false,
          showPriceDate: branch.settings?.showPriceDate === true,
          priceUpdatedAt:
            branch.settings?.priceUpdatedAt ?? dayjs().format("YYYY-MM-DD"),
          gaMeasurementId: branch.settings?.gaMeasurementId ?? "",
          reviewPlaceId: branch.settings?.reviewPlaceId ?? "",
          theme: branch.settings?.theme || "default",
        },
      });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, branch]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        name: values.name.trim(),
        slug: values.slug.trim(),
        // uid yalnızca formun içinde yaşıyor (satır kimliği); alan alan yeniden kurulduğu
        // için DB'ye sızmıyor.
        // Normalleştirme KAYDETMEDE de yapılıyor, yalnızca alandan çıkarken değil:
        // kullanıcı satıra hiç dokunmadan "Kaydet"e bastığında (ör. yalnızca şube adını
        // değiştirdi) blur hiç tetiklenmiyor ve ham değer aynen yazılıyordu — düz numara
        // menüde `https://5075971811` oluyor ve şube kartında telefon çipi çıkmıyordu.
        // İşlem idempotent: şemalı adrese dokunmuyor.
        links: values.links
          .filter((link) => link.icon && link.value.trim())
          .map((link) => ({
            icon: link.icon,
            value: normalizeLinkValue(link.value),
            active: link.active !== false,
          })),
        settings: {
          ...values.settings,
          priceUpdatedAt: values.settings.priceUpdatedAt || "",
          gaMeasurementId: values.settings.gaMeasurementId.trim(),
          reviewPlaceId: values.settings.reviewPlaceId.trim(),
        },
      };
      return isEdit ? updateBranch(branch.id, payload) : createBranch(payload);
    },
    onSuccess: () => {
      notify.success(isEdit ? "Değişiklikler kaydedildi" : "Şube oluşturuldu");
      queryClient.invalidateQueries({ queryKey: ["branches"] });
      onClose();
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "İşlem başarısız"),
  });

  // Sekmeler form durumunun dışında: geçiş alanları sıfırlamıyor, yalnızca hangisinin görüneceğini
  // seçiyor. Pencere her açıldığında ilk sekmeye dönüyor — bir önceki şubede "Bağlantılar"da
  // kalmışken yeni şube o sekmeyle açılsa kullanıcı adı girmeyi atlardı.
  const [tab, setTab] = useState("info");

  useEffect(() => {
    if (opened) {
      setTab("info");
    }
  }, [opened]);

  // Hangi sekmede hata var. Doğrulama hatası gizli bir sekmedeyse kullanıcı "Kaydet"e basıp
  // hiçbir şey olmadığını görüyordu — Mantine odağı görünmeyen alana taşıyor ve ekranda hiçbir
  // iz kalmıyor. Nokta sekmede, ayrıca geçersiz gönderimde o sekmeye atlanıyor (aşağıda).
  const TAB_FIELDS = {
    info: ["name", "slug"],
    settings: [
      "settings.priceUpdatedAt",
      "settings.gaMeasurementId",
      "settings.reviewPlaceId",
    ],
  };
  const tabHasError = (key) =>
    (TAB_FIELDS[key] ?? []).some((field) => form.errors[field]);

  // Bağlantılar TEK dizide duruyor (Branch.links) ama iki farklı işi var, o yüzden formda ikiye
  // ayrılıyor:
  //   konum  → yalnızca şube seçim kartındaki "Yol Tarifi"; menünün ikon satırında yok
  //   diğeri → menünün ikon satırı, sırası ve 8'lik sınırı burada
  //
  // Konum satırı önce SİMGEDEN tanınıyor, sonra adresten: kullanıcı yuvaya geçici olarak
  // yarım bir adres yazdığında satır listeye kaçmasın. Adres kontrolü eski kayıtlar için —
  // onlar 'maps' anahtarıyla yazılmıştı.
  const locationIndex = form.values.links.findIndex(isLocationLink);
  const locationLink =
    locationIndex >= 0 ? form.values.links[locationIndex] : null;
  // Menüde görünenler, MUTLAK index'leriyle: sürükleme ve alan yolları tam diziye göre çalışıyor.
  const menuLinks = form.values.links
    .map((link, index) => ({ link, index }))
    .filter((entry) => !isLocationLink(entry.link));

  const setLocation = (value) => {
    if (locationIndex >= 0) {
      form.setFieldValue(`links.${locationIndex}.value`, value);

      return;
    }

    if (value.trim()) {
      form.insertListItem("links", {
        uid: linkUid(),
        icon: `${BRAND_PREFIX}maps`,
        value,
        active: true,
      });
    }
  };

  const linkSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleLinkDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    const from = form.values.links.findIndex((link) => link.uid === active.id);
    const to = form.values.links.findIndex((link) => link.uid === over.id);
    if (from !== -1 && to !== -1) {
      form.reorderListItem("links", { from, to });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      // lg → xl: bağlantı satırındaki adres alanı en çok yeri isteyen kontrol ve artık TAM
      // adres yazılıyor (tel:+90…, uzun Google Maps bağlantıları). lg'de görünen kısım
      // adresin yarısı kadardı.
      size="xl"
      padding="lg"
      title={
        <ModalTitle
          icon={IconBuildingStore}
          title={isEdit ? "Şube Düzenle" : "Yeni Şube"}
          // Düzenlemede slug bağlam veriyor: hangi şubede olduğun ve QR adresinin ne
          // olduğu tek satırda görünüyor.
          description={
            isEdit
              ? `${branch.name} · /${branch.slug}`
              : "Ad, bağlantılar ve menü ayarları"
          }
        />
      }
    >
      {/* Geçersiz gönderimde hatanın olduğu sekmeye atla. Aksi halde "Kaydet" hiçbir şey
                yapmıyormuş gibi görünüyor: Mantine odağı hata alanına taşıyor ama o alan başka bir
                sekmede, yani ekranda. */}
      <form
        onSubmit={form.onSubmit(
          (values) => mutation.mutate(values),
          (errors) =>
            setTab(
              Object.keys(errors).some((field) => field.startsWith("settings."))
                ? "settings"
                : "info",
            ),
        )}
      >
        <Stack gap="md">
          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            <Tabs.List mb="md">
              <Tabs.Tab
                value="info"
                leftSection={<IconBuildingStore size={16} />}
                rightSection={tabHasError("info") ? <ErrorDot /> : null}
              >
                Bilgiler
              </Tabs.Tab>
              <Tabs.Tab
                value="links"
                leftSection={<IconLink size={16} />}
                // Sayaç sekmede: kaç bağlantı olduğunu ve sınıra ne kadar
                // kaldığını görmek için sekmeyi açmak gerekmiyor.
                rightSection={
                  <Badge size="xs" variant="light" color="gray" circle>
                    {menuLinks.length}
                  </Badge>
                }
              >
                Bağlantılar
              </Tabs.Tab>
              <Tabs.Tab
                value="settings"
                leftSection={<IconAdjustments size={16} />}
                rightSection={tabHasError("settings") ? <ErrorDot /> : null}
              >
                Menü Ayarları
              </Tabs.Tab>
            </Tabs.List>

            {/* Sabit alt sınır: sekmeler farklı yükseklikte ve pencere her geçişte
                            zıplıyordu. */}
            <Box mih={300}>
              <Tabs.Panel value="info">
                {/* Dar ekranda alt alta. İkisinin de `description`'ı var: Mantine
                                    açıklamayı etiket ile girdinin ARASINA koyuyor, biri açıklamasız
                                    kalsaydı iki girdi farklı hizada dururdu. */}
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <TextInput
                    label="Şube Adı"
                    description="Menüde ve şube seçim kartında görünür."
                    placeholder="Örn. Kadıköy"
                    {...form.getInputProps("name")}
                  />
                  <TextInput
                    label="Slug"
                    description={slugHint}
                    placeholder="kadikoy"
                    {...form.getInputProps("slug")}
                  />
                </SimpleGrid>
              </Tabs.Panel>

              <Tabs.Panel value="links">
                <Stack gap="lg">
                  {/* Konum AYRI: menünün ikon satırında hiç görünmüyor (harita
                                        zaten "Şubelerimiz" listesindeki her satırın kendi
                                        bağlantısı), yalnızca şube seçim kartında "Yol Tarifi"
                                        çipini besliyor. Tek listede dururken 8'lik kotayı yiyor ama
                                        menüde çıkmıyordu — sayı kullanıcıya yalan söylüyordu. */}
                  <Stack gap={6}>
                    <Text size="sm" fw={600}>
                      Konum
                    </Text>
                    <TextInput
                      leftSection={
                        <img
                          src="/brands/maps.svg"
                          alt=""
                          width={16}
                          height={16}
                        />
                      }
                      placeholder="https://maps.google.com/..."
                      value={locationLink?.value ?? ""}
                      onChange={(event) =>
                        setLocation(event.currentTarget.value)
                      }
                    />
                  </Stack>

                  <Divider />

                  <Stack gap="sm">
                    <div>
                      <Text size="sm" fw={600}>
                        Menü Bağlantıları
                      </Text>
                      <Text size="xs" c="dimmed">
                        Menüdeki ikon satırında bu sırayla görünür — en fazla{" "}
                        {MAX_LINKS}. Simge seç, adresi yaz; sırayı sürükleyerek
                        değiştir.
                      </Text>
                    </div>
                    <DndContext
                      sensors={linkSensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleLinkDragEnd}
                    >
                      <SortableContext
                        items={menuLinks.map((entry) => entry.link.uid)}
                        strategy={verticalListSortingStrategy}
                      >
                        {menuLinks.map((entry) => (
                          <SortableLinkRow
                            key={entry.link.uid}
                            id={entry.link.uid}
                            index={entry.index}
                            form={form}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                    {menuLinks.length === 0 && (
                      <Text size="sm" c="dimmed" ta="center" py="lg">
                        Henüz bağlantı yok.
                      </Text>
                    )}
                    {menuLinks.length < MAX_LINKS && (
                      <Button
                        variant="light"
                        size="xs"
                        leftSection={<IconPlus size={14} />}
                        onClick={() =>
                          form.insertListItem("links", {
                            uid: linkUid(),
                            icon: DEFAULT_LINK_EMOJI,
                            value: "",
                            active: true,
                          })
                        }
                      >
                        Bağlantı Ekle
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="settings">
                <Stack gap="sm">
                  {/* İkisi de "menüde şu blok görünsün mü" sorusunu soruyor —
                                        yan yana durunca aynı aileden oldukları okunuyor. */}
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    <Switch
                      label="Şubelerimiz listesi"
                      description="Diğer şubeler menüde listelensin"
                      color="teal"
                      {...form.getInputProps("settings.showBranches", {
                        type: "checkbox",
                      })}
                    />
                    <Switch
                      label="İletişim & bağlantılar"
                      description="Telefon, harita, sosyal medya ikonları gösterilsin"
                      color="teal"
                      {...form.getInputProps("settings.showLinks", {
                        type: "checkbox",
                      })}
                    />
                  </SimpleGrid>
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Switch
                      label="Fiyat güncelleme tarihi"
                      description="Menüde tarih notu"
                      color="teal"
                      {...form.getInputProps("settings.showPriceDate", {
                        type: "checkbox",
                      })}
                    />
                    {form.values.settings.showPriceDate && (
                      <DateInput
                        w={180}
                        valueFormat="DD.MM.YYYY"
                        placeholder="Tarih seç"
                        clearable
                        {...form.getInputProps("settings.priceUpdatedAt")}
                      />
                    )}
                  </Group>
                  <TextInput
                    label="Google Analytics ID"
                    description="Bu şubenin QR menüsüne özel GA4 kodu (G-XXXXXXXXXX). Boş = kapalı."
                    placeholder="G-XXXXXXXXXX"
                    {...form.getInputProps("settings.gaMeasurementId")}
                  />
                  <TextInput
                    label="Google Place ID"
                    description="Bu şubenin Google kaydının kimliği. Google Place ID Finder'da işletmeyi aratıp kopyalayın. Menüde “Deneyimini paylaş” bağlantısı çıkar. Boş = gizli."
                    placeholder="ChIJ..."
                    {...form.getInputProps("settings.reviewPlaceId")}
                  />
                  <Select
                    label="Menü Tasarımı"
                    description="Bu şubenin QR menüsünün görünümü"
                    withCheckIcon={false}
                    allowDeselect={false}
                    data={[{ value: "default", label: "Varsayılan" }]}
                    {...form.getInputProps("settings.theme")}
                  />
                </Stack>
              </Tabs.Panel>
            </Box>
          </Tabs>

          <FormModalFooter>
            <Button variant="default" onClick={onClose}>
              Vazgeç
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              leftSection={
                isEdit ? <IconDeviceFloppy size={16} /> : <IconPlus size={16} />
              }
            >
              {isEdit ? "Kaydet" : "Oluştur"}
            </Button>
          </FormModalFooter>
        </Stack>
      </form>
    </Modal>
  );
}

// Local-state price input; saved only when the save button next to it is clicked.
// `current` is the stored override value (null = inherits). When `fallback` is given the
// field prefills with the effective price (so it is never blank); a value equal to the
// fallback is treated as "no override" (null).
function OverridePriceInput({
  current,
  saving,
  onSave,
  placeholder = "taban",
  fallback = null,
  disabled = false,
  hideReset = false,
}) {
  const initial = current ?? fallback ?? "";
  const [value, setValue] = useState(initial);

  useEffect(() => {
    setValue(current ?? fallback ?? "");
  }, [current, fallback]);

  const raw = value === "" || value === null ? null : Number(value);
  const next = fallback != null && raw === fallback ? null : raw;
  // An override equal to the fallback is functionally "no override" — normalise the stored value
  // the same way so the buttons stay disabled for a redundant override (e.g. one the product-level
  // push wrote at exactly the base price).
  const normalizedCurrent =
    fallback != null && current === fallback ? null : current;
  const dirty = next !== normalizedCurrent;
  const hasOverride = normalizedCurrent != null;

  return (
    <Group gap={6} wrap="nowrap">
      <NumberInput
        size="xs"
        min={0}
        hideControls
        placeholder={placeholder}
        w={90}
        value={value}
        onChange={setValue}
        disabled={disabled}
      />
      <Tooltip label="Fiyatı kaydet" withArrow>
        <ActionIcon
          aria-label="Fiyatı kaydet"
          size="sm"
          variant={dirty ? "filled" : "light"}
          disabled={disabled || !dirty}
          loading={saving}
          onClick={() => onSave(next)}
        >
          <IconDeviceFloppy size={14} />
        </ActionIcon>
      </Tooltip>
      {!hideReset && (
        <Tooltip label="Varsayılana dön" withArrow>
          <ActionIcon
            aria-label="Varsayılana dön"
            size="sm"
            variant="subtle"
            color="gray"
            disabled={disabled || !hasOverride}
            onClick={() => {
              setValue(fallback ?? "");
              onSave(null);
            }}
          >
            <IconArrowBackUp size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}

// Fiyat tablosunun satırı; sürükleme kolu ilk hücrede. Satırın geri kalanı (fiyat inputları)
// children olarak geliyor ki tablo mantığı tek yerde kalsın.
function SortableOverrideRow({ id, disabled, hint, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  return (
    <ListRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
        background: isDragging ? "var(--mantine-color-chocolate-0)" : undefined,
      }}
    >
      <ListCell w={32}>
        <ActionIcon
          size="sm"
          variant="subtle"
          color="gray"
          {...attributes}
          {...listeners}
          disabled={disabled}
          title={hint}
          style={{
            cursor: disabled ? "not-allowed" : "grab",
            touchAction: "none",
          }}
        >
          <IconGripVertical size={14} />
        </ActionIcon>
      </ListCell>
      {children}
    </ListRow>
  );
}

function BranchMenuModal({ branch, opened, onClose }) {
  const queryClient = useQueryClient();
  const { can } = useAccess();
  // Sıralama da fiyat/görünürlük gibi bir şube override'ı — API'de prices:update.
  const canOrder = can("products") || can("categories");
  const productSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  // Fiyatlar tablosu ve sıralama aynı ekranın iki yüzü; şube satırına ikinci bir buton koymak
  // yerine sekme.
  const [tab, setTab] = useState("prices");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { data: products = EMPTY_LIST, isLoading: productsLoading } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });
  const { data: categories = EMPTY_LIST } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
  const { data: overrides = EMPTY_LIST, isLoading: overridesLoading } =
    useQuery({
      queryKey: ["branch-overrides", branch.id],
      queryFn: () => getBranchOverrides(branch.id),
    });
  const { data: connections = EMPTY_LIST } = useQuery({
    queryKey: ["connections"],
    queryFn: getConnections,
  });

  // Products mapped to any of this branch's integrations — Ent. Fiyat is only
  // editable for these (a price with nowhere to go makes no sense).
  const branchConnections = useMemo(
    () => connections.filter((connection) => connection.branchId === branch.id),
    [connections, branch.id],
  );
  const mappingQueries = useQueries({
    queries: branchConnections.map((connection) => ({
      queryKey: ["mappings", connection.id],
      queryFn: () => getMappings(connection.id),
    })),
  });
  // productId -> current integration price (first mapping found). Also tells us which
  // products are bound (has()), so Ent. Fiyat is only editable for those.
  const mappedPriceByProductId = useMemo(() => {
    const map = new Map();
    for (const query of mappingQueries) {
      for (const mapping of query.data ?? []) {
        if (mapping.remoteId && !map.has(mapping.productId)) {
          map.set(mapping.productId, mapping.price);
        }
      }
    }
    return map;
  }, [mappingQueries]);

  const mutation = useMutation({
    mutationFn: ({ productId, payload }) =>
      upsertBranchOverride(branch.id, productId, payload),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ["branch-overrides", branch.id],
      }),
    onError: () => notify.error("Güncellenemedi"),
  });

  // Per-row: push this product's current integration price to the branch's channels now.
  const pushMutation = useMutation({
    mutationFn: (productId) => pushBranchProductPrice(branch.id, productId),
    onSuccess: (result) => {
      const { pushed, connections: connectionCount, failed = [] } = result;

      if (failed.length) {
        // Kanal reddetti (ör. Trendyol recurring-price) — genel mesaj, ham hatayı basma.
        notify.error(
          pushed
            ? `${pushSummary(pushed, result)} · ${failed.length} entegrasyon reddetti`
            : "Gönderilemedi",
        );
      } else if (!connectionCount) {
        notify.info("Şubede aktif bağlantı yok");
      } else if (!pushed) {
        notify.info("Fiyat entegrasyondakiyle aynı, gönderilmedi");
      } else {
        notify.success(pushSummary(pushed, result));
      }
    },
    onError: () => notify.error("Fiyat gönderilemedi"),
  });

  // Gönderim uçarken pencere kapanabilirse kullanıcı iptal ettiğini sanıyor: istek sürüyor,
  // sonucu ve hatayı hiç görmüyor.
  const closeIfIdle = () => {
    if (!pushMutation.isPending) {
      onClose();
    }
  };

  const hasConnections = branchConnections.length > 0;

  const overrideFor = (productId) =>
    overrides.find((item) => item.productId === productId);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "Tüm kategoriler" },
      ...categories.map((item) => ({
        value: String(item.id),
        label: item.name,
      })),
    ],
    [categories],
  );

  // Ürünler sayfasıyla aynı davranış: ilk ÜRÜNÜ OLAN kategoriyle açılır. Sadece categories[0]
  // seçmek, o kategori boşsa modalı boş tabloyla açıyordu — filtre çalışmıyor gibi görünüyordu.
  const [categoryInitialized, setCategoryInitialized] = useState(false);
  useEffect(() => {
    if (categoryInitialized || !categories.length || !products.length) {
      return;
    }

    const withProducts = new Set(products.map((product) => product.categoryId));
    const firstWithProducts = categories.find((item) =>
      withProducts.has(item.id),
    );

    setCategory(String((firstWithProducts ?? categories[0]).id));
    setCategoryInitialized(true);
  }, [categories, products, categoryInitialized]);

  // Tablo menüdeki sırayı gösterir: şubeye özel sıra varsa o, yoksa markanınki. Aksi halde
  // kullanıcı burada sürüklerken menüde başka bir dizilim olurdu.
  const rankByProductId = useMemo(
    () =>
      new Map(
        overrides.map((override) => [
          override.productId,
          override.rank ?? null,
        ]),
      ),
    [overrides],
  );
  const orderedProducts = useMemo(
    () => byBranchRank(products, (product) => rankByProductId.get(product.id)),
    [products, rankByProductId],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orderedProducts.filter(
      (product) =>
        (category === "all" || product.categoryId === Number(category)) &&
        (!term || product.name.toLowerCase().includes(term)),
    );
  }, [orderedProducts, category, search]);

  // Sıralama tek bir kategori içinde anlamlı: ürün sırası kategori kategori tutuluyor.
  const sortableCategoryId = category === "all" ? null : Number(category);
  const canSortProducts = canOrder && sortableCategoryId !== null;
  const sortHint =
    sortableCategoryId === null
      ? "Sıralamak için bir kategori seç"
      : canOrder
        ? "Sürükleyerek sırala"
        : null;

  const categoryProducts = useMemo(
    () =>
      orderedProducts.filter(
        (product) => product.categoryId === sortableCategoryId,
      ),
    [orderedProducts, sortableCategoryId],
  );
  // Override satırı olmayan üründe get() undefined döner; `!== null` her zaman doğru olduğu için
  // buton hiç kilitlenmiyordu.
  const productOrderCustomized = categoryProducts.some(
    (product) => (rankByProductId.get(product.id) ?? null) !== null,
  );

  const productRankMutation = useMutation({
    mutationFn: (next) =>
      setBranchProductRanks(
        branch.id,
        next.map((item, index) => ({ productId: item.id, rank: index })),
      ),
    onError: () => notify.error("Sıra kaydedilemedi"),
    onSettled: () =>
      void queryClient.invalidateQueries({
        queryKey: ["branch-overrides", branch.id],
      }),
  });

  const productOrderResetMutation = useMutation({
    mutationFn: () =>
      resetBranchProductRanks(
        branch.id,
        categoryProducts.map((product) => product.id),
      ),
    onSuccess: () => notify.success("Ürün sırası markaya döndürüldü"),
    onError: () => notify.error("Sıra sıfırlanamadı"),
    onSettled: () =>
      void queryClient.invalidateQueries({
        queryKey: ["branch-overrides", branch.id],
      }),
  });

  // Sıra HER ZAMAN kategorinin tam listesi üzerinden yazılır; tablo sayfalı ve aranabilir olduğu
  // için sürüklenen ürün, ekranda bırakıldığı komşunun gerçek konumuna taşınır.
  const handleProductDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    productRankMutation.mutate(
      moveBeside(categoryProducts, active.id, over.id),
    );
  };

  useEffect(() => {
    setPage(1);
  }, [search, category, perPage, branch.id]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <Modal
      opened={opened}
      onClose={closeIfIdle}
      closeOnClickOutside={!pushMutation.isPending}
      closeOnEscape={!pushMutation.isPending}
      closeButtonProps={{
        disabled: pushMutation.isPending,
        title: pushMutation.isPending ? "Gönderim sürüyor" : undefined,
      }}
      centered
      radius="md"
      size="72rem"
      title={
        <div>
          <Text fw={600}>{branch.name} — şube menüsü</Text>
          <Text size="xs" c="dimmed">
            {tab === "order"
              ? "Kategorileri sürükleyerek bu şubeye özel sıra ver"
              : "Fiyat girip kaydet; boş bırakılan ürün taban/entegrasyon fiyatıyla satılır. Satırları sürükleyerek bu şubenin menü sırasını değiştir."}
          </Text>
        </div>
      }
    >
      <Tabs value={tab} onChange={setTab} mb="md">
        <Tabs.List grow>
          <Tabs.Tab value="prices" leftSection={<IconCoin size={16} />}>
            Fiyatlar
          </Tabs.Tab>
          <Tabs.Tab value="order" leftSection={<IconArrowsSort size={16} />}>
            Kategori Sırası
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {tab === "order" ? (
        <BranchCategoryOrder branch={branch} />
      ) : productsLoading || overridesLoading ? (
        <Group justify="center" p="xl">
          <Loader />
        </Group>
      ) : (
        <>
          <Group mb="md" justify="center">
            <TextInput
              w={240}
              size="xs"
              placeholder="Ürün ara"
              leftSection={<IconSearch size={14} />}
              value={search}
              onChange={(event) => setSearch(event.currentTarget.value)}
            />
            <Select
              withCheckIcon={false}
              w={220}
              size="xs"
              searchable
              allowDeselect={false}
              data={categoryOptions}
              value={category}
              onChange={setCategory}
            />
          </Group>
          <Card withBorder radius="md" p={0}>
            <ListScroll minWidth={807} fit={false}>
              <ListHeader
                columns={[
                  { label: "", w: 32 },
                  { label: "Ürün", flex: true, ta: "left" },
                  { label: "Şube Fiyatı", w: 175 },
                  { label: "Eski Fiyat", w: 175 },
                  { label: "Ent. Fiyat", w: 175 },
                  { label: "Gönder", w: 70 },
                ]}
              />
              <DndContext
                sensors={productSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleProductDragEnd}
              >
                <SortableContext
                  items={pageItems.map((product) => product.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {pageItems.map((product) => {
                    const override = overrideFor(product.id);
                    // Only the field actually being saved spins — şube ve ent birbirine bağlı değil.
                    const savingKey =
                      mutation.isPending &&
                      mutation.variables?.productId === product.id
                        ? Object.keys(mutation.variables.payload ?? {})[0]
                        : null;

                    return (
                      <SortableOverrideRow
                        key={product.id}
                        id={product.id}
                        disabled={!canSortProducts}
                        hint={sortHint}
                      >
                        <ListCell flex ta="left">
                          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                            <Avatar
                              src={product.imageUrl}
                              radius="md"
                              size={32}
                            >
                              <IconPhoto size={16} stroke={1.4} />
                            </Avatar>
                            <Text size="sm" truncate>
                              {product.name}
                            </Text>
                          </Group>
                        </ListCell>
                        <ListCell w={175}>
                          <OverridePriceInput
                            current={override?.priceOverride ?? null}
                            fallback={product.basePrice}
                            saving={savingKey === "priceOverride"}
                            onSave={(next) =>
                              mutation.mutate({
                                productId: product.id,
                                payload: { priceOverride: next },
                              })
                            }
                          />
                        </ListCell>
                        <ListCell w={175}>
                          <OverridePriceInput
                            current={override?.oldPriceOverride ?? null}
                            fallback={product.oldPrice ?? null}
                            placeholder="eski fiyat"
                            saving={savingKey === "oldPriceOverride"}
                            onSave={(next) =>
                              mutation.mutate({
                                productId: product.id,
                                payload: { oldPriceOverride: next },
                              })
                            }
                          />
                        </ListCell>
                        <ListCell w={175}>
                          <OverridePriceInput
                            current={override?.integrationPriceOverride ?? null}
                            fallback={product.integrationPrice ?? null}
                            saving={savingKey === "integrationPriceOverride"}
                            disabled={!hasConnections}
                            hideReset
                            onSave={(next) =>
                              mutation.mutate({
                                productId: product.id,
                                payload: { integrationPriceOverride: next },
                              })
                            }
                          />
                        </ListCell>
                        <ListCell w={70}>
                          <Tooltip
                            label={
                              !hasConnections
                                ? "Şubede bağlantı yok"
                                : !mappedPriceByProductId.has(product.id)
                                  ? "Ürün entegrasyona eşleşmemiş"
                                  : "Fiyatı entegrasyona gönder"
                            }
                            withArrow
                          >
                            <ActionIcon
                              aria-label="Fiyatı entegrasyona gönder"
                              size="sm"
                              variant="light"
                              color="teal"
                              disabled={
                                !hasConnections ||
                                !mappedPriceByProductId.has(product.id)
                              }
                              loading={
                                pushMutation.isPending &&
                                pushMutation.variables === product.id
                              }
                              onClick={() => pushMutation.mutate(product.id)}
                            >
                              <IconSend size={14} />
                            </ActionIcon>
                          </Tooltip>
                        </ListCell>
                      </SortableOverrideRow>
                    );
                  })}
                </SortableContext>
              </DndContext>
              {pageItems.length === 0 && <ListEmpty>Ürün bulunamadı</ListEmpty>}
            </ListScroll>
          </Card>
          {/* Üç kolon: sayfa ayarları solda, sıfırlama gerçekten ortada, pagination sağda.
                        Sıra kategori bazlı olduğu için buton yalnızca seçili kategoriyi döndürür. */}
          <Box
            mt="sm"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: "var(--mantine-spacing-sm)",
            }}
          >
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" c="dimmed">
                Sayfa başı
              </Text>
              <Select
                withCheckIcon={false}
                w={75}
                size="xs"
                allowDeselect={false}
                data={PER_PAGE_OPTIONS}
                value={String(perPage)}
                onChange={(value) => setPerPage(Number(value))}
              />
              <Text size="sm" c="dimmed">
                {filtered.length} ürün
              </Text>
            </Group>
            <Button
              size="compact-sm"
              variant="subtle"
              leftSection={<IconArrowsSort size={14} />}
              loading={productOrderResetMutation.isPending}
              {...lockProps(canSortProducts, {
                disabled: !productOrderCustomized,
              })}
              onClick={() => productOrderResetMutation.mutate()}
            >
              Marka sırasına dön
            </Button>
            <Group justify="flex-end" wrap="nowrap">
              <Pagination
                withEdges
                total={totalPages}
                value={page}
                onChange={setPage}
                size="sm"
              />
            </Group>
          </Box>
        </>
      )}
    </Modal>
  );
}

export default function Branches() {
  const { allBranches } = useAccess();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const {
    data: branches = EMPTY_LIST,
    isLoading,
    isError,
    refetch,
  } = useQuery({ queryKey: ["branches"], queryFn: getBranches });
  const [selectedId, setSelectedId] = useState(null);
  const [formOpened, { open: openForm, close: closeForm }] =
    useDisclosure(false);
  const [formBranch, setFormBranch] = useState(null);
  const [qrBranch, setQrBranch] = useState(null);
  const [rootQrOpen, setRootQrOpen] = useState(false);
  const [imageBranch, setImageBranch] = useState(null);

  // Sürükle-bırak sıralama için local kopya (optimistic); branches DB'den rank sıralı gelir.
  const [items, setItems] = useState([]);
  useEffect(() => {
    setItems([...branches].sort((a, b) => a.rank - b.rank));
  }, [branches]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const rankMutation = useMutation({
    mutationFn: updateBranchRank,
    onSuccess: () => notify.success("Sıralama kaydedildi"),
    onError: () =>
      notify.error("Sıralama kaydedilemedi — liste eski sırasına döndü"),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ["branches"] }),
  });

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = items.findIndex((branch) => branch.id === active.id);
    const newIndex = items.findIndex((branch) => branch.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    rankMutation.mutate(
      reordered.map((branch, index) => ({ id: branch.id, rank: index })),
    );
  };

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) => updateBranch(id, { isActive }),
    onSuccess: (_data, { name, isActive }) => {
      notify.success(
        `${name} şubesi ${isActive ? "satışa açıldı" : "kapatıldı"}`,
      );
      queryClient.invalidateQueries({ queryKey: ["branches"] });
    },
    onError: () => notify.error("Güncellenemedi"),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteBranch,
    onSuccess: () => {
      notify.success("Şube silindi");
      queryClient.invalidateQueries({ queryKey: ["branches"] });
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Şube silinemedi"),
  });

  const confirmDelete = (branch) =>
    modals.openConfirmModal({
      title: "Şubeyi sil",
      centered: true,
      children: (
        <Text size="sm">
          <b>{branch.name}</b> şubesi, tüm entegrasyon bağlantıları,
          eşleştirmeleri ve şube-bazlı fiyat/menü ayarlarıyla birlikte kalıcı
          olarak silinecek. Bu işlem geri alınamaz.
        </Text>
      ),
      labels: { confirm: "Sil", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(branch.id),
    });

  const selected = branches.find((branch) => branch.id === selectedId) ?? null;

  return (
    <Stack gap="lg">
      <PageTitle>Şubeler</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconBuildingStore size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>Şubeler</Title>
            <Text c="dimmed" size="sm">
              QR menü adresleri ve şube bazlı fiyat/menü yönetimi
            </Text>
          </div>
        </Group>
        <Group gap="sm">
          <Button
            variant="default"
            leftSection={<IconQrcode size={16} />}
            onClick={() => setRootQrOpen(true)}
          >
            Ana Menü QR
          </Button>
          {/* Şube oluşturmak şube kapsamına sığmaz — API'de de requireFullAccess korur.
                        Yetkisiz kullanıcıda gizlenmiyor, kilitleniyor. */}
          <Button
            leftSection={<IconPlus size={16} />}
            {...lockProps(allBranches)}
            onClick={() => {
              setFormBranch(null);
              openForm();
            }}
          >
            Yeni Şube
          </Button>
        </Group>
      </Group>

      <Card withBorder radius="md" p="md">
        {isError ? (
          <ListError title="Şubeler alınamadı" onRetry={refetch} />
        ) : isLoading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : (
          <Card withBorder radius="md" p={0}>
            <ListScroll minWidth={816}>
              <ListHeader
                columns={[
                  { spacer: true, w: 36 },
                  { label: "Ad", flex: true },
                  { label: "Durum", w: 70 },
                  { label: "İşlem", w: 510 },
                ]}
              />
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={items.map((branch) => branch.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {items.map((branch) => (
                    <SortableBranchRow
                      key={branch.id}
                      branch={branch}
                      tenantSlug={user?.tenantSlug}
                      toggling={
                        toggleMutation.isPending &&
                        toggleMutation.variables?.id === branch.id
                      }
                      onToggle={(item, isActive) =>
                        toggleMutation.mutate({
                          id: item.id,
                          name: item.name,
                          isActive,
                        })
                      }
                      onQr={setQrBranch}
                      onImage={setImageBranch}
                      onEdit={(item) => {
                        setFormBranch(item);
                        openForm();
                      }}
                      onManage={(item) => setSelectedId(item.id)}
                      onDelete={confirmDelete}
                      deleting={
                        deleteMutation.isPending &&
                        deleteMutation.variables === branch.id
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {items.length === 0 && <ListEmpty>Şube yok</ListEmpty>}
            </ListScroll>
          </Card>
        )}
      </Card>

      {selected && (
        <BranchMenuModal
          branch={selected}
          opened
          onClose={() => setSelectedId(null)}
        />
      )}

      <BranchFormModal
        opened={formOpened}
        onClose={closeForm}
        branch={formBranch}
      />
      <QrModal
        opened={Boolean(qrBranch)}
        onClose={() => setQrBranch(null)}
        url={
          qrBranch
            ? `https://${user?.tenantSlug}.noxussoft.com/${qrBranch.slug}`
            : null
        }
        label={qrBranch?.name ?? ""}
      />
      <ImageUploadModal
        opened={Boolean(imageBranch)}
        onClose={() => setImageBranch(null)}
        title="Şube Görseli"
        idField="id"
        idValue={imageBranch?.id}
        uploadFn={uploadBranchImage}
        currentImageUrl={imageBranch?.imageUrl}
        removeFn={removeBranchImage}
        invalidateKey={["branches"]}
        crop={IMAGE_TARGETS.branch}
      />
      <QrModal
        opened={rootQrOpen}
        onClose={() => setRootQrOpen(false)}
        url={rootQrOpen ? `https://${user?.tenantSlug}.noxussoft.com` : null}
        label="Ana Menü"
      />
    </Stack>
  );
}
